import { 
  CheckCircle2, 
  Ban, 
  ArrowDown, 
  MessageSquare,
  Users,
  FileText,
  Clock,
  Target
} from "lucide-react";

const correctBehaviors = [
  {
    scenario: "You notice a bug in production",
    wrong: "Slack the developer who wrote the code asking them to fix it",
    right: "Tell the Engineering Manager → they assign and prioritize it",
    why: "The manager needs to know the team's workload and can prioritize appropriately"
  },
  {
    scenario: "You disagree with a technical decision",
    wrong: "Tell the developer their approach is wrong in a group channel",
    right: "Discuss concerns with the Tech Lead or Engineering Manager privately",
    why: "Public criticism from executives destroys psychological safety"
  },
  {
    scenario: "You need a quick status update on a project",
    wrong: "Ask the individual contributors directly in Slack",
    right: "Ask the Project Manager or Team Lead who owns the project",
    why: "ICs shouldn't feel monitored by executives; let managers aggregate status"
  },
  {
    scenario: "You have feedback about an employee's performance",
    wrong: "Give the feedback directly to that employee",
    right: "Share the feedback with their manager to deliver appropriately",
    why: "Managers need to own the relationship with their direct reports"
  },
  {
    scenario: "You want to assign an urgent task",
    wrong: "Drop it directly into a developer's queue",
    right: "Tell the manager what needs to happen → they figure out who and when",
    why: "Only the manager knows team capacity and current commitments"
  },
  {
    scenario: "You're excited about a new idea and want it built",
    wrong: "Describe it directly to a developer in the hallway",
    right: "Bring it to Product → they work with Engineering Manager on feasibility",
    why: "Ideas need proper scoping before hitting engineering; don't create side quests"
  }
];

export const DepartmentalAutonomyPlaybook = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 id="proper-chain" className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
        <ArrowDown className="h-6 w-6 text-primary" />
        How to Communicate Down the Chain Properly
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        The rule is simple: <strong>always go through the manager first</strong>. Here's how to handle common situations correctly:
      </p>

      <div className="space-y-4">
        {correctBehaviors.map((item, index) => (
          <div 
            key={item.scenario}
            className="bg-card border border-border rounded-lg p-5"
          >
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm shrink-0">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-3">{item.scenario}</h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-3">
                  <div className="flex items-start gap-2 bg-destructive/5 border border-destructive/10 rounded-lg p-3">
                    <Ban className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <div>
                      <span className="text-xs font-medium text-destructive uppercase tracking-wide">Wrong</span>
                      <p className="text-sm text-muted-foreground">{item.wrong}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 bg-primary/5 border border-primary/10 rounded-lg p-3">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">Right</span>
                      <p className="text-sm text-muted-foreground">{item.right}</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  <strong>Why:</strong> {item.why}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Golden Rule */}
      <div className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          The Golden Rule
        </h3>
        <p className="text-lg text-foreground mb-2">
          <strong>Before contacting anyone, ask: "Who is their manager?"</strong>
        </p>
        <p className="text-muted-foreground">
          Then go to that manager first. Every. Single. Time. Even when it feels slower. Even when you have a good relationship with the IC. Even when it's "just a quick question." The only exception is genuine emergencies or HR/ethics issues.
        </p>
      </div>
    </section>
  );
};
